import React, { useState } from 'react';
import { GoogleMap, Circle, Polygon, OverlayView } from '@react-google-maps/api';
import { toast } from 'react-hot-toast';
import * as ShapeUtils from '../../utils/shapeManipulationUtils';

const MapView = ({
  mapContainerStyle,
  mapCenter,
  map,
  setMap,
  setMapBounds,
  handleMapClick,
  searchMode,
  visibleAreas,
  selectedArea,
  setSelectedArea,
  searchedShapes,
  isDrawingMode,
  isShapeDragging,
  currentShape,
  shapeInteractionMode,
  setIsDragging,
  generatedCircles,
  selectedPolygonCircles,
  handlePolygonCircleSelection,
  shapeDragStart,
  shapeSize,
  tubeThickness,
  setCurrentShape,
  shapeHistory,
  historyIndex,
  setShapeHistory,
  setHistoryIndex,
  setIsShapeDragging,
  setShapeDragStart,
  handleDeleteCircle
}) => {
  const [hoveredCircleId, setHoveredCircleId] = useState(null);

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={mapCenter}
      zoom={12}
      onLoad={(mapInstance) => {
        setMap(mapInstance);
        const bounds = mapInstance.getBounds();
        setMapBounds(bounds);
      }}
      onClick={handleMapClick}
      onBoundsChanged={() => {
        if (map) {
          const bounds = map.getBounds();
          setMapBounds(bounds);
        }
      }}
      options={{
        streetViewControl: false,
        mapTypeControl: true,
        fullscreenControl: true,
        gestureHandling: 'greedy',
      }}
    >
      {/* Regular Circle Areas - Only show if in circles mode */}
      {searchMode === 'circles' && visibleAreas.map((area) => (
        <React.Fragment key={`${area.id}-${area.has_been_searched ? 'searched' : 'unsearched'}`}>
          <Circle
            center={{
              lat: parseFloat(area.latitude),
              lng: parseFloat(area.longitude),
            }}
            radius={area.radius}
            onClick={() => setSelectedArea(area)}
            onMouseOver={() => {
              if (!area.has_been_searched) {
                setHoveredCircleId(area.id);
              }
            }}
            onMouseOut={() => setHoveredCircleId(null)}
            options={{
              fillColor: area.id === selectedArea?.id 
                ? '#3b82f6' 
                : area.has_been_searched 
                  ? '#10b981' 
                  : hoveredCircleId === area.id
                    ? '#ef4444'
                    : '#6b7280',
              fillOpacity: hoveredCircleId === area.id ? 0.3 : 0.2,
              strokeColor: area.id === selectedArea?.id 
                ? '#3b82f6' 
                : area.has_been_searched 
                  ? '#10b981' 
                  : hoveredCircleId === area.id
                    ? '#ef4444'
                    : '#6b7280',
              strokeOpacity: 0.8,
              strokeWeight: hoveredCircleId === area.id ? 3 : 2,
              clickable: searchMode === 'circles',
              draggable: false,
              editable: false,
              visible: true,
            }}
          />
          
          {/* Delete button overlay for unsearched circles on hover */}
          {!area.has_been_searched && hoveredCircleId === area.id && (
            <OverlayView
              position={{
                lat: parseFloat(area.latitude),
                lng: parseFloat(area.longitude),
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteCircle(area.id);
                  setHoveredCircleId(null);
                }}
                className="bg-red-600 hover:bg-red-700 opacity-75 text-white rounded-full p-2 shadow-lg cursor-pointer transition-all"
                style={{ 
                  position: 'absolute',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1000 
                }}
                title="Delete this circle"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
            </OverlayView>
          )}
        </React.Fragment>
      ))}

      {/* Previously Searched Shapes */}
      {searchedShapes.map((searchedShape, index) => {
        if (!searchedShape.shape_data || !searchedShape.shape_data.outerPoints) {
          return null;
        }
        
        const isSearched = searchedShape.has_been_searched;
        const shapeType = searchedShape.shape_type === 'pentagon' ? 'polygon' : 'tube';
        
        return (
          <React.Fragment key={`${searchedShape.id}-${index}`}>
            {/* Outer Polygon */}
            <Polygon
              paths={searchedShape.shape_data.outerPoints}
              options={{
                fillColor: isSearched ? '#6b7280' : (shapeType === 'polygon' ? '#8b5cf6' : '#f59e0b'),
                fillOpacity: isSearched ? 0.15 : (shapeType === 'polygon' ? 0.2 : 0.15),
                strokeColor: isSearched ? '#6b7280' : (shapeType === 'polygon' ? '#8b5cf6' : '#f59e0b'),
                strokeOpacity: isSearched ? 0.6 : 0.8,
                strokeWeight: isSearched ? 2 : 3,
                clickable: !isDrawingMode && !isShapeDragging
              }}
              onClick={() => {
                if (isSearched && !isDrawingMode && !isShapeDragging) {
                  const searchDate = searchedShape.last_searched_at 
                    ? new Date(searchedShape.last_searched_at).toLocaleDateString()
                    : 'Unknown date';
                  toast(`This ${shapeType} area was searched on ${searchDate}`, {
                    icon: 'ℹ️',
                    duration: 4000,
                    style: {
                      background: '#e0f2fe',
                      color: '#0c4a6e',
                      border: '1px solid #0ea5e9',
                    },
                  });
                }
              }}
            />
            
            {/* Inner Polygon (for tube shapes) */}
            {shapeType === 'tube' && searchedShape.shape_data.innerPoints && (
              <Polygon
                paths={searchedShape.shape_data.innerPoints}
                options={{
                  fillColor: '#6b7280',   
                  fillOpacity: 0.1,   
                  strokeColor: '#000000',
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  clickable: !isDrawingMode && !isShapeDragging
                }}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Current Shape (Active Drawing) */}
      {currentShape && (searchMode === 'polygon' || searchMode === 'tube') && (
        <>
          {/* Outer Pentagon */}
          <Polygon
            paths={currentShape.outerPoints}
            options={{
              fillColor: searchMode === 'polygon' ? '#8b5cf6' : '#f59e0b',
              fillOpacity: searchMode === 'polygon' ? 0.2 : 0.15,
              strokeColor: searchMode === 'polygon' ? '#8b5cf6' : '#f59e0b',
              strokeOpacity: 0.8,
              strokeWeight: 3,
              draggable: shapeInteractionMode === 'move',
              cursor: shapeInteractionMode === 'move' 
                ? (isShapeDragging ? 'grabbing' : 'grab') 
                : 'default',
            }}
            onDragStart={(e) => ShapeUtils.handleShapeDragStart(e, currentShape, shapeInteractionMode, setIsShapeDragging, setShapeDragStart)}
            onDrag={(e) => ShapeUtils.handleShapeDrag(e, isShapeDragging, shapeDragStart, currentShape, shapeInteractionMode, searchMode, shapeSize, tubeThickness, setCurrentShape)}
            onDragEnd={() => ShapeUtils.handleShapeDragEnd(isShapeDragging, currentShape, shapeHistory, historyIndex, setIsShapeDragging, setShapeDragStart, setShapeHistory, setHistoryIndex)}
          />
          
          {/* Inner Pentagon (for tube shape) */}
          {searchMode === 'tube' && currentShape.innerPoints && (
            <Polygon
              paths={currentShape.innerPoints}
              options={{
                fillColor: '#6b7280',  
                fillOpacity: 0.1,     
                strokeColor: '#000000', 
                strokeOpacity: 0.8,
                strokeWeight: 2,
                draggable: shapeInteractionMode === 'move',
                cursor: shapeInteractionMode === 'move' 
                  ? (isShapeDragging ? 'grabbing' : 'grab') 
                  : 'default',
              }}
              onDragStart={(e) => ShapeUtils.handleShapeDragStart(e, currentShape, shapeInteractionMode, setIsShapeDragging, setShapeDragStart)}
              onDrag={(e) => ShapeUtils.handleShapeDrag(e, isShapeDragging, shapeDragStart, currentShape, shapeInteractionMode, searchMode, shapeSize, tubeThickness, setCurrentShape)}
              onDragEnd={() => ShapeUtils.handleShapeDragEnd(isShapeDragging, currentShape, shapeHistory, historyIndex, setIsShapeDragging, setShapeDragStart, setShapeHistory, setHistoryIndex)}
            />
          )}
          
          {/* Draggable corner markers for outer pentagon */}
          {shapeInteractionMode === 'reshape' && currentShape.outerPoints?.map((point, index) => (
            <Circle
              key={`outer-${index}`}
              center={point}
              radius={150}
              options={{
                fillColor: searchMode === 'polygon' ? '#8b5cf6' : '#f59e0b',
                fillOpacity: 0.8,
                strokeColor: '#ffffff',
                strokeOpacity: 1,
                strokeWeight: 3,
                clickable: true,
                draggable: true,
                cursor: 'move',
              }}
              onDragStart={() => setIsDragging(true)}
              onDrag={() => setIsDragging(true)}
              onDragEnd={(e) => {
                ShapeUtils.handleCornerDrag(e, index, false, currentShape, shapeInteractionMode, searchMode, setCurrentShape);
                ShapeUtils.handleCornerDragEnd(currentShape, shapeHistory, historyIndex, setIsDragging, setShapeHistory, setHistoryIndex);
              }}
            />
          ))}
          
          {/* Draggable corner markers for inner pentagon (tube only) */}
          {shapeInteractionMode === 'reshape' && searchMode === 'tube' && currentShape.innerPoints?.map((point, index) => (
            <Circle
              key={`inner-${index}`}
              center={point}
              radius={100}
              options={{
                fillColor: '#ef4444',
                fillOpacity: 0.8,
                strokeColor: '#ffffff',
                strokeOpacity: 1,
                strokeWeight: 2,
                clickable: true,
                draggable: true,
                cursor: 'move',
              }}
              onDragStart={() => setIsDragging(true)}
              onDrag={() => setIsDragging(true)}
              onDragEnd={(e) => {
                ShapeUtils.handleCornerDrag(e, index, true, currentShape, shapeInteractionMode, searchMode, setCurrentShape);
                ShapeUtils.handleCornerDragEnd(currentShape, shapeHistory, historyIndex, setIsDragging, setShapeHistory, setHistoryIndex);
              }}
            />
          ))}
        </>
      )}

      {/* Generated Circles for Polygon/Tube */}
      {generatedCircles.map((circle) => (
        <Circle
          key={circle.id}
          center={{
            lat: circle.center_point.latitude,
            lng: circle.center_point.longitude,
          }}
          radius={circle.radius}
          options={{
            fillColor: selectedPolygonCircles.has(circle.id) 
              ? '#10b981' 
              : '#3b82f6',
            fillOpacity: selectedPolygonCircles.has(circle.id) ? 0.3 : 0.2,
            strokeColor: selectedPolygonCircles.has(circle.id) 
              ? '#10b981' 
              : '#3b82f6',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            clickable: !isDrawingMode && !isShapeDragging,
          }}
          onClick={() => {
            if (!isDrawingMode && !isShapeDragging) {
              const isSelected = selectedPolygonCircles.has(circle.id);
              handlePolygonCircleSelection(circle.id, !isSelected);
            }
          }}
        />
      ))}
    </GoogleMap>
  );
};

export default MapView;